import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, FileText, ExternalLink, TrendingUp, DollarSign } from "lucide-react";
import { formatCurrency } from "@/components/peo/dashboard/utils/quoteUtils";
import { format } from "date-fns";

interface MobileQuoteCardProps {
  quote: any;
  onAction?: (quote: any, action: 'interested' | 'accepted' | 'declined') => void;
  showEnhancedMetrics?: boolean;
  metrics?: any;
}

export const MobileQuoteCard = ({ quote, onAction, showEnhancedMetrics, metrics }: MobileQuoteCardProps) => {
  const handleAction = (action: 'interested' | 'accepted' | 'declined') => {
    onAction?.(quote, action);
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg leading-tight">{quote.provider_name || "PEO Provider"}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Quote for {quote.company_name || "your application"}
            </p>
          </div>
          <QuoteStatusBadge status={quote.status} />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Cost Information - Stacked for mobile */}
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Monthly Cost</span>
            </div>
            <span className="font-semibold">
              {quote.monthly_cost ? formatCurrency(quote.monthly_cost) : "Not specified"}
            </span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Annual Cost</span>
            </div>
            <span className="font-semibold">
              {quote.annual_cost ? formatCurrency(quote.annual_cost) : "Not specified"}
            </span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Valid Until</span>
            </div>
            <span className="font-semibold">
              {quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : "Not specified"}
            </span>
          </div>
        </div>

        {/* Enhanced Metrics for mobile */}
        {showEnhancedMetrics && metrics && (
          <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <h4 className="text-sm font-medium text-primary">Savings Analysis</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Potential Savings:</span>
                <p className="font-semibold">{formatCurrency(metrics.savings_amount || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Implementation:</span>
                <p className="font-semibold">{metrics.implementation_days || 0} days</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Quote Summary */}
        {quote.quote_summary && (
          <div className="p-3 bg-muted/50 rounded-lg border">
            <p className="text-sm">{quote.quote_summary}</p>
          </div>
        )}
        
        {/* Action Buttons - Stacked on mobile */}
        <div className="space-y-2">
          {quote.quote_file_url && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(quote.quote_file_url, '_blank')}
              className="w-full justify-center gap-2"
            >
              <FileText className="h-4 w-4" />
              View Quote Document
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          
          {quote.status === 'pending' && (
            <div className="grid grid-cols-1 gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleAction('interested')}
                className="w-full"
              >
                Mark Interested
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  size="sm" 
                  variant="default"
                  onClick={() => handleAction('accepted')}
                  className="bg-primary hover:bg-primary/90"
                >
                  Accept
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => handleAction('declined')}
                >
                  Decline
                </Button>
              </div>
            </div>
          )}
          
          {quote.status === 'interested' && (
            <div className="grid grid-cols-2 gap-2">
              <Button 
                size="sm" 
                variant="default"
                onClick={() => handleAction('accepted')}
                className="bg-primary hover:bg-primary/90"
              >
                Accept Quote
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => handleAction('declined')}
              >
                Decline
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const QuoteStatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'accepted':
      return <Badge className="bg-primary">Accepted</Badge>;
    case 'declined':
      return <Badge variant="destructive">Declined</Badge>;
    case 'interested':
      return <Badge className="bg-secondary">Interested</Badge>;
    default:
      return <Badge variant="outline">Pending</Badge>;
  }
};